import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { RotateCcw, ImageIcon } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';

const getMediaUrl = (src) => resolveMediaUrl(src) || '';

const shuffleWithSeed = (array, seed) => {
  const result = [...array];
  const seededRandom = (s) => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  };
  let seedValue = seed;
  for (let i = result.length - 1; i > 0; i--) {
    seedValue++;
    const j = Math.floor(seededRandom(seedValue) * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

const DragLabelImageElement = ({
  element,
  isEditor = false,
  studentView = false,
  placements: externalPlacements = null,
  showResult = false,
  perSlotResults = null,
  onPlacementsChange,
  onCheck,
  onRetry,
  disabled = false,
  hideCheckButton = false,
}) => {
  const { t } = useTranslation();
  const { content, style, id } = element;
  const {
    imageUrl = '',
    slots = [],
    labels = [],
    paletteSide = 'top',
    allowRetry = false,
    slotColor = '#2A4D25',
    labelColor = '#ffffff',
    labelTextColor = '#1f2937',
    fontFamily = 'Inter',
    fontSize = 14,
    cropRect = null, // { x, y, w, h } in % of source image; null = no crop
  } = content || {};
  const backgroundColor = style?.backgroundColor || '#ffffff';

  const hasCrop = cropRect && cropRect.w > 0 && cropRect.h > 0 &&
    !(cropRect.x === 0 && cropRect.y === 0 && cropRect.w === 100 && cropRect.h === 100);

  // Stable shuffle of labels per element so order is consistent
  const shuffledLabels = useMemo(() => {
    if (!labels || labels.length === 0) return [];
    const seed = id ? id.split('').reduce((a, c) => a + c.charCodeAt(0), 0) : 1;
    return shuffleWithSeed(labels, seed);
  }, [labels, id]);

  // Width of the widest label, used as min-width for slots so the dashed slot
  // is at least as wide as the longest pill it could hold.
  const labelMinWidth = useMemo(() => {
    if (!labels || labels.length === 0) return 80;
    if (typeof document === 'undefined') {
      const longest = labels.reduce((m, l) => Math.max(m, (l?.text || '').length), 0);
      return Math.max(80, Math.round(longest * fontSize * 0.55) + 28);
    }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = `500 ${fontSize}px "${fontFamily}", Inter, system-ui, sans-serif`;
    let maxW = 0;
    labels.forEach((l) => {
      const w = ctx.measureText(l?.text || '').width;
      if (w > maxW) maxW = w;
    });
    return Math.max(80, Math.ceil(maxW) + 32);
  }, [labels, fontFamily, fontSize]);

  // Largest slot dimensions (in % of painted image), so every drop zone and
  // every palette pill can render at the same (biggest) size in student view.
  const uniformSlotWidthPct = useMemo(
    () => (slots.length ? slots.reduce((m, s) => Math.max(m, s.width || 0), 0) : 0),
    [slots]
  );
  const uniformSlotHeightPct = useMemo(
    () => (slots.length ? slots.reduce((m, s) => Math.max(m, s.height || 0), 0) : 0),
    [slots]
  );

  // placements: { [slotId]: labelId | null }
  const [placements, setPlacements] = useState(externalPlacements || {});
  useEffect(() => {
    if (externalPlacements) setPlacements(externalPlacements);
  }, [externalPlacements]);

  const [draggedLabelId, setDraggedLabelId] = useState(null);
  const [dragOverSlotId, setDragOverSlotId] = useState(null);
  const [naturalSize, setNaturalSize] = useState(null); // { w, h } natural image dims
  const [containerSize, setContainerSize] = useState(null); // { w, h } image-area px size
  const containerRef = useRef(null);
  const loaderRef = useRef(null);
  // Touch/pen drag state — HTML5 drag events do not fire on mobile browsers,
  // so we run a parallel pointer-based drag with a floating ghost element and
  // elementFromPoint hit-testing to find the target slot.
  const pointerDragRef = useRef(null); // { labelId, ghostEl, pointerId }

  // Reset natural size when the image url changes
  useEffect(() => { setNaturalSize(null); }, [imageUrl]);

  // Fall back to reading the cached image's intrinsic size if onLoad already
  // fired before React attached the handler.
  useEffect(() => {
    const img = loaderRef.current;
    if (img && img.complete && img.naturalWidth && img.naturalHeight) {
      setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    }
  }, [imageUrl]);

  // Observe the image-area size so we can compute the painted (object-contain) box
  // and pin slots to it. ResizeObserver fires when the slide widget is resized.
  //
  // Why offsetWidth/offsetHeight (not getBoundingClientRect): the classroom
  // renders the slide inside a `transform: scale(boardScale)` subtree, especially
  // on mobile where the scale is small. getBoundingClientRect returns post-transform
  // viewport pixels, but we apply the returned dimensions as CSS `width`/`height`
  // *inside* that scaled subtree — the browser then scales them again, producing
  // an undersized painted box and misaligned slots. offsetWidth/offsetHeight give
  // the pre-transform layout box, which matches ResizeObserver contentRect.
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const apply = (w, h) => {
      if (w > 0 && h > 0) setContainerSize({ w, h });
    };
    apply(el.offsetWidth, el.offsetHeight);
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) apply(e.contentRect.width, e.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Painted box: where the (possibly-cropped) image is actually rendered inside
  // the image-area. Slots position themselves relative to this box, so they
  // stay glued to the image when the widget is resized.
  const paintedBox = useMemo(() => {
    if (!containerSize || !naturalSize) return null;
    const cropW = hasCrop ? cropRect.w : 100;
    const cropH = hasCrop ? cropRect.h : 100;
    const visibleAspect = (naturalSize.w * cropW) / (naturalSize.h * cropH);
    if (!isFinite(visibleAspect) || visibleAspect <= 0) return null;
    const containerAspect = containerSize.w / containerSize.h;
    let w, h;
    if (visibleAspect > containerAspect) {
      w = containerSize.w;
      h = w / visibleAspect;
    } else {
      h = containerSize.h;
      w = h * visibleAspect;
    }
    return {
      w, h,
      left: (containerSize.w - w) / 2,
      top: (containerSize.h - h) / 2,
    };
  }, [containerSize, naturalSize, hasCrop, cropRect]);

  // Pixel size of a "uniform" pill — matches the largest drop zone once the
  // painted image box is measured. Palette pills use this so they render at
  // the same size as the drop zones under the pictures.
  const uniformPillPx = useMemo(() => {
    if (!paintedBox) return { w: labelMinWidth, h: null };
    const w = Math.max(labelMinWidth, Math.ceil((uniformSlotWidthPct / 100) * paintedBox.w));
    const h = Math.ceil((uniformSlotHeightPct / 100) * paintedBox.h);
    return { w, h: h > 0 ? h : null };
  }, [paintedBox, uniformSlotWidthPct, uniformSlotHeightPct, labelMinWidth]);

  // On narrow viewports the fixed 150px slot min-width would push each drop
  // zone wider than the painted image can accommodate (N slots × 150px >
  // paintedBox.w), making the dashed outlines overflow and overlap. Cap
  // min-width so all slots together stay within the painted image.
  const slotMinWidth = useMemo(() => {
    if (!paintedBox || !slots.length) return labelMinWidth;
    const share = Math.max(40, Math.floor(paintedBox.w / slots.length) - 8);
    return Math.min(labelMinWidth, share);
  }, [paintedBox, labelMinWidth, slots.length]);

  const placedLabelIds = useMemo(() => {
    return new Set(Object.values(placements).filter(Boolean));
  }, [placements]);

  const allPlaced = !isEditor && shuffledLabels.length > 0 && shuffledLabels.every((l) => placedLabelIds.has(l.id));

  const updatePlacements = useCallback((next) => {
    setPlacements(next);
    onPlacementsChange?.(next);
  }, [onPlacementsChange]);

  const handleDragStart = (e, labelId) => {
    if (disabled || showResult || isEditor) return;
    // Order matters in Safari: setData must come before effectAllowed
    try { e.dataTransfer.setData('text/plain', labelId); } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';

    // Safari can't render a drag image when the dragged element lives inside a
    // `transform: scale(...)` subtree (the slide is rendered scaled), so the
    // drag visually never starts. Render a clone on document.body and use it
    // as the drag image so WebKit has something to attach the pointer to.
    const label = labels.find((l) => l.id === labelId);
    if (label && typeof document !== 'undefined' && e.dataTransfer.setDragImage) {
      const ghost = document.createElement('div');
      ghost.textContent = label.text;
      Object.assign(ghost.style, {
        position: 'fixed',
        top: '-1000px',
        left: '-1000px',
        padding: '4px 12px',
        borderRadius: '9999px',
        border: `2px solid ${slotColor}`,
        background: labelColor,
        color: labelTextColor,
        font: `500 ${fontSize}px "${fontFamily}", Inter, system-ui, sans-serif`,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      });
      document.body.appendChild(ghost);
      const rect = ghost.getBoundingClientRect();
      e.dataTransfer.setDragImage(ghost, rect.width / 2, rect.height / 2);
      setTimeout(() => { ghost.remove(); }, 0);
    }

    setDraggedLabelId(labelId);
  };

  const handleDragEnd = () => {
    setDraggedLabelId(null);
    setDragOverSlotId(null);
  };

  const handleSlotDragOver = (e, slotId) => {
    if (disabled || showResult || isEditor) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSlotId(slotId);
  };

  const handleSlotDragLeave = () => {
    setDragOverSlotId(null);
  };

  const handleSlotDrop = (e, slotId) => {
    if (disabled || showResult || isEditor) return;
    e.preventDefault();
    const labelId = draggedLabelId || e.dataTransfer.getData('text/plain');
    if (!labelId) return;

    // Remove the label from any other slot it currently occupies
    const next = { ...placements };
    Object.keys(next).forEach((sid) => {
      if (next[sid] === labelId) next[sid] = null;
    });
    next[slotId] = labelId;
    updatePlacements(next);
    setDraggedLabelId(null);
    setDragOverSlotId(null);
  };

  // Click on a placed label to send it back to the palette
  const handlePlacedClick = (slotId) => {
    if (disabled || showResult || isEditor) return;
    if (!placements[slotId]) return;
    const next = { ...placements };
    next[slotId] = null;
    updatePlacements(next);
  };

  // Start a pointer-based drag for touch/pen input. Mouse still uses native
  // HTML5 drag so we don't regress desktop behavior.
  const handlePointerDown = useCallback((e, labelId) => {
    if (disabled || showResult || isEditor) return;
    if (e.pointerType === 'mouse') return;
    if (pointerDragRef.current) return;
    const label = labels.find((l) => l.id === labelId);
    if (!label) return;
    e.preventDefault();

    const ghost = document.createElement('div');
    ghost.textContent = label.text;
    Object.assign(ghost.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      padding: '4px 12px',
      borderRadius: '9999px',
      border: `2px solid ${slotColor}`,
      background: labelColor,
      color: labelTextColor,
      font: `500 ${fontSize}px "${fontFamily}", Inter, system-ui, sans-serif`,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
      zIndex: '99999',
      opacity: '0.95',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      willChange: 'transform',
    });
    document.body.appendChild(ghost);
    const rect = ghost.getBoundingClientRect();
    ghost.style.transform = `translate(${e.clientX - rect.width / 2}px, ${e.clientY - rect.height / 2}px)`;

    pointerDragRef.current = { labelId, ghostEl: ghost, pointerId: e.pointerId };
    setDraggedLabelId(labelId);
  }, [disabled, showResult, isEditor, labels, slotColor, labelColor, labelTextColor, fontFamily, fontSize]);

  // Global pointermove/up while a touch drag is active. Re-attached whenever
  // placements/updatePlacements change so the drop handler always sees the
  // latest state — synchronous cleanup means no events are dropped mid-drag.
  useEffect(() => {
    const findSlotIdAt = (clientX, clientY) => {
      const drag = pointerDragRef.current;
      if (!drag) return null;
      const prevDisplay = drag.ghostEl.style.display;
      drag.ghostEl.style.display = 'none';
      const el = typeof document !== 'undefined' ? document.elementFromPoint(clientX, clientY) : null;
      drag.ghostEl.style.display = prevDisplay;
      const slotEl = el && el.closest ? el.closest('[data-drag-slot-id]') : null;
      return slotEl ? slotEl.getAttribute('data-drag-slot-id') : null;
    };

    const cleanupDrag = () => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      if (drag.ghostEl && drag.ghostEl.parentNode) drag.ghostEl.parentNode.removeChild(drag.ghostEl);
      pointerDragRef.current = null;
      setDraggedLabelId(null);
      setDragOverSlotId(null);
    };

    const onMove = (e) => {
      const drag = pointerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      e.preventDefault();
      const rect = drag.ghostEl.getBoundingClientRect();
      drag.ghostEl.style.transform = `translate(${e.clientX - rect.width / 2}px, ${e.clientY - rect.height / 2}px)`;
      setDragOverSlotId(findSlotIdAt(e.clientX, e.clientY));
    };

    const onUp = (e) => {
      const drag = pointerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      const slotId = findSlotIdAt(e.clientX, e.clientY);
      const labelId = drag.labelId;
      cleanupDrag();
      if (slotId) {
        const next = { ...placements };
        Object.keys(next).forEach((sid) => {
          if (next[sid] === labelId) next[sid] = null;
        });
        next[slotId] = labelId;
        updatePlacements(next);
      }
    };

    const onCancel = (e) => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      if (e && e.pointerId != null && e.pointerId !== drag.pointerId) return;
      cleanupDrag();
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [placements, updatePlacements]);

  // Ensure ghost is removed if the component unmounts mid-drag.
  useEffect(() => () => {
    const drag = pointerDragRef.current;
    if (drag && drag.ghostEl && drag.ghostEl.parentNode) {
      drag.ghostEl.parentNode.removeChild(drag.ghostEl);
    }
    pointerDragRef.current = null;
  }, []);

  const handleCheck = () => {
    onCheck?.(placements);
  };

  const handleRetry = () => {
    setPlacements({});
    onRetry?.();
  };

  const allSlotsFilled = slots.length > 0 && slots.every((s) => placements[s.id]);

  // Layout: palette on top/bottom/left/right
  const isHorizontalPalette = paletteSide === 'top' || paletteSide === 'bottom';
  const flexDirection = paletteSide === 'top' ? 'column'
    : paletteSide === 'bottom' ? 'column-reverse'
    : paletteSide === 'left' ? 'row'
    : 'row-reverse';

  const getLabelText = (labelId) => labels.find((l) => l.id === labelId)?.text || '';

  const renderLabelPill = (label, opts = {}) => {
    const {
      isDraggable = true,
      isInSlot = false,
      slotResult = null,
      slotId = null,
      width = null,
      height = null,
      fill = false,
    } = opts;

    let bg = labelColor;
    let textColor = labelTextColor;
    let borderColor = labelColor;
    if (isInSlot && showResult && slotResult !== null) {
      if (slotResult === true) {
        bg = '#dcfce7';
        textColor = '#166534';
        borderColor = '#22c55e';
      } else if (slotResult === false) {
        bg = '#fee2e2';
        textColor = '#991b1b';
        borderColor = '#ef4444';
      }
    }

    const dragEnabled = isDraggable && !disabled && !showResult && !isEditor;
    const sizeStyle = fill
      ? { width: '100%', height: '100%' }
      : {
          ...(width != null ? { width: `${width}px` } : {}),
          ...(height != null ? { height: `${height}px` } : {}),
        };
    const touchDragEnabled = dragEnabled && !isInSlot;
    return (
      <div
        key={label.id + (isInSlot ? '_in_' + slotId : '_pal')}
        draggable={dragEnabled}
        onDragStart={(e) => handleDragStart(e, label.id)}
        onDragEnd={handleDragEnd}
        onPointerDown={touchDragEnabled ? (e) => handlePointerDown(e, label.id) : undefined}
        onClick={isInSlot ? () => handlePlacedClick(slotId) : undefined}
        className={`
          ${fill ? 'flex' : 'inline-flex'} items-center justify-center px-3 py-1.5 rounded-full
          text-sm font-medium border-2 ${fill ? 'text-center leading-tight' : 'whitespace-nowrap'}
          ${dragEnabled ? 'cursor-grab active:cursor-grabbing' : ''}
          ${isInSlot && !showResult && !disabled && !isEditor ? 'cursor-pointer' : ''}
          ${draggedLabelId === label.id ? 'opacity-40' : ''}
          transition-colors duration-100
        `}
        style={{
          backgroundColor: bg,
          color: textColor,
          borderColor,
          fontFamily,
          fontSize: `${fontSize}px`,
          ...sizeStyle,
          WebkitUserDrag: dragEnabled ? 'element' : 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          // Prevents the browser from claiming touch gestures (scroll/zoom)
          // before pointermove reaches us on mobile.
          touchAction: touchDragEnabled ? 'none' : undefined,
        }}
      >
        {label.text}
      </div>
    );
  };

  // Empty/placeholder state for editor when image not uploaded
  if (!imageUrl) {
    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300"
        style={{ backgroundColor }}
      >
        <ImageIcon size={40} className="text-gray-300 mb-2" />
        <p className="text-sm text-gray-400">{t('games.dragLabel.uploadImageInSettings')}</p>
      </div>
    );
  }

  const hideChrome = !isEditor;

  return (
    <div
      className={`w-full h-full rounded-xl overflow-hidden flex ${
        hideChrome ? '' : 'border-2 shadow-sm'
      }`}
      style={{
        backgroundColor: hideChrome ? 'transparent' : backgroundColor,
        flexDirection,
        borderColor: '#e5e7eb',
      }}
    >
      {/* Palette — stays visible after check so the surrounding layout
          doesn't shift. */}
      <div
        className={`relative flex ${isHorizontalPalette ? 'flex-row flex-wrap' : 'flex-col'} gap-2 p-3 bg-gray-50/80 ${isHorizontalPalette ? '' : 'min-w-[120px]'}`}
        style={{
          alignItems: 'center',
          justifyContent: isHorizontalPalette ? 'center' : 'flex-start',
        }}
      >
        {shuffledLabels.map((label) => {
          const isPlaced = !isEditor && placedLabelIds.has(label.id);
          return (
            <span
              key={label.id}
              style={{
                visibility: isPlaced ? 'hidden' : 'visible',
                pointerEvents: isPlaced ? 'none' : undefined,
              }}
            >
              {renderLabelPill(label, {
                width: uniformPillPx.w,
                height: uniformPillPx.h,
              })}
            </span>
          );
        })}
        {allPlaced && (
          <span className="absolute inset-0 flex items-center justify-center text-xs text-gray-400 pointer-events-none">
            {t('games.dragLabel.allLabelsPlaced')}
          </span>
        )}
      </div>

      {/* Image area. Image is fit-in (object-contain). Slots are pinned to the
          painted box (computed via ResizeObserver) so they scale with the image
          when the widget is resized. */}
      <div
        ref={containerRef}
        className="relative flex-1 min-h-0 min-w-0 bg-white overflow-hidden"
      >
        {/* Hidden loader to read natural dimensions */}
        <img
          ref={loaderRef}
          src={getMediaUrl(imageUrl)}
          alt=""
          draggable={false}
          onLoad={(e) => {
            const w = e.currentTarget.naturalWidth;
            const h = e.currentTarget.naturalHeight;
            if (w && h) setNaturalSize({ w, h });
          }}
          style={{ display: 'none' }}
        />
        {paintedBox && (
          <div
            className="absolute overflow-hidden"
            style={{
              left: `${paintedBox.left}px`,
              top: `${paintedBox.top}px`,
              width: `${paintedBox.w}px`,
              height: `${paintedBox.h}px`,
            }}
          >
            <img
              src={getMediaUrl(imageUrl)}
              alt=""
              draggable={false}
              className="pointer-events-none select-none"
              style={hasCrop ? {
                position: 'absolute',
                width: `${10000 / cropRect.w}%`,
                height: `${10000 / cropRect.h}%`,
                left: `${-cropRect.x * 100 / cropRect.w}%`,
                top: `${-cropRect.y * 100 / cropRect.h}%`,
                maxWidth: 'none',
              } : {
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
              }}
            />
            {/* Slots */}
            {slots.map((slot) => {
              const placedLabelId = placements[slot.id];
              const slotResult = showResult && perSlotResults ? perSlotResults[slot.id] : null;
              const isOver = dragOverSlotId === slot.id;
              // In student view, every drop zone renders at the largest
              // configured slot size so the exercise looks uniform.
              const displayWidthPct = !isEditor && uniformSlotWidthPct > 0 ? uniformSlotWidthPct : slot.width;
              const displayHeightPct = !isEditor && uniformSlotHeightPct > 0 ? uniformSlotHeightPct : slot.height;

              return (
                <div
                  key={slot.id}
                  data-drag-slot-id={!isEditor && !showResult ? slot.id : undefined}
                  onDragOver={(e) => handleSlotDragOver(e, slot.id)}
                  onDragLeave={handleSlotDragLeave}
                  onDrop={(e) => handleSlotDrop(e, slot.id)}
                  className="absolute flex items-center justify-center rounded-full border-2 border-dashed transition-colors"
                  style={{
                    left: `${slot.x}%`,
                    top: `${slot.y}%`,
                    width: `${displayWidthPct}%`,
                    height: `${displayHeightPct}%`,
                    minWidth: `${slotMinWidth}px`,
                    transform: 'translate(-50%, -50%)',
                    borderColor: slotResult === true ? '#22c55e' : slotResult === false ? '#ef4444' : slotColor,
                    backgroundColor: isOver ? 'rgba(42, 77, 37, 0.12)' : 'rgba(255,255,255,0.6)',
                  }}
                >
                  {placedLabelId ? (
                    renderLabelPill(
                      labels.find((l) => l.id === placedLabelId) || { id: placedLabelId, text: getLabelText(placedLabelId) },
                      { isDraggable: false, isInSlot: true, slotResult, slotId: slot.id, fill: true }
                    )
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Action bar absolute at bottom-right */}
      {!isEditor && (
        <div className="absolute bottom-2 right-2 z-10 flex items-center gap-2">
          {showResult && allowRetry && perSlotResults && !Object.values(perSlotResults).every(v => v === true) && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg shadow-sm transition"
            >
              <RotateCcw size={14} />
              {t('games.common.tryAgain')}
            </button>
          )}
          {!showResult && !hideCheckButton && (
            <button
              onClick={handleCheck}
              disabled={!allSlotsFilled}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg shadow-sm transition ${
                allSlotsFilled
                  ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {t('games.common.check')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DragLabelImageElement;
